import { useEffect, useState } from "react";
import { FaRegSave, FaRegTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchTripTimeline,
    getAllGeofence,
    deleteGeofence,
    getTripDetails,
    getTripLogs,
    saveGeofence,
    setSelectedTripId,
    setViewAllTrips,
    viewAllTrips as fetchViewAllTrips,
} from "../../store/Slices/TripSlice";
import "../../styles/Trip.css";
import { errorNotify, successNotify } from "../../utils/toast";
import FleetOverviewMapLibre from "../test/FleetOverviewMapLibre";

const TripMap = ({ detailView = false, customCenter = null }) => {
    const dispatch = useDispatch();

    const { tripListsData, viewAllTrips, activeTab, getAllGeofenceData } = useSelector((state) => state.trips);

    const [geofences, setGeofences] = useState([
        {
            id: 1,
            center: { lat: 30.3753, lng: 69.3451 }, // Pakistan Center
            radius: 5000,
            options: {
                strokeColor: "#F0A842",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#F0A842",
                fillOpacity: 0.30,
                clickable: true,
                draggable: true,
                editable: true,
                visible: true,
                zIndex: 1
            }
        }
    ]);
    const [isGeofenceMode, setIsGeofenceMode] = useState(false);
    const [selectedGeofence, setSelectedGeofence] = useState(null);

    useEffect(() => {
        dispatch(getAllGeofence())
    }, [])

    useEffect(() => {
        if (Array.isArray(getAllGeofenceData)) {
            const formattedGeofences = getAllGeofenceData.map(geo => ({
                id: geo.id || geo.location_id || geo._id,
                center: { 
                    lat: Number(geo.lat || geo.latitude || 0), 
                    lng: Number(geo.lng || geo.longitude || 0) 
                },
                radius: Number(geo.radius || 0) * 1000, 
                address: geo.location || geo.location_name || geo.address,
                options: {
                    strokeColor: "#F0A842",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: "#F0A842",
                    fillOpacity: 0.30,
                    clickable: true,
                    draggable: true,
                    editable: true,
                    visible: true,
                    zIndex: 1
                }
            }));
            setGeofences(formattedGeofences);
        }
    }, [getAllGeofenceData]);

    const onToggleViewAll = (checked) => {
        dispatch(setViewAllTrips(checked));

        if (!checked) {
            const firstId = tripListsData?.[0]?.id;
            if (firstId) {
                dispatch(setSelectedTripId(firstId));
                dispatch(fetchTripTimeline(firstId));
                dispatch(getTripDetails(firstId));
                dispatch(getTripLogs(firstId));
            }
        } else {
            dispatch(setSelectedTripId(null));
            dispatch(fetchViewAllTrips());
        }
    };

    const handleDeleteGeofence = () => {
        if (selectedGeofence) {
            // Check if it's a temporary local ID (timestamps are very large numbers)
            const isTemporary = typeof selectedGeofence.id === 'number' && selectedGeofence.id > 1000000000000;

            if (isTemporary) {
                // If temporary, just remove from local state
                setGeofences(prev => prev.filter(geo => geo.id !== selectedGeofence.id));
                setSelectedGeofence(null);
                successNotify("Unsaved geofence removed");
                return;
            }

            // If it's a DB ID, call the API
            dispatch(deleteGeofence(selectedGeofence.id))
                .unwrap()
                .then(() => {
                    successNotify("Geofence deleted successfully");
                    setGeofences(prev => prev.filter(geo => geo.id !== selectedGeofence.id));
                    setSelectedGeofence(null);
                    dispatch(getAllGeofence()); // Refresh from API to be sure
                })
                .catch((err) => {
                    errorNotify(err || "Failed to delete geofence");
                    // Optionally keep it in UI if delete failed
                    // setGeofences(prev => prev.filter(geo => geo.id !== selectedGeofence.id));
                    // setSelectedGeofence(null);
                });
        }
    };

    const handleSaveGeofence = () => {
        if (selectedGeofence) {

            const payload = {
                location: selectedGeofence?.address,
                location_id: selectedGeofence.id,
                radius: Math.round(selectedGeofence.radius / 1000),
                // radiusMeters: selectedGeofence.radius,
                lat: selectedGeofence.center.lat,
                lng: selectedGeofence.center.lng,
                type: "allowed"
            };

            dispatch(saveGeofence(payload))
                .unwrap()
                .then((data) => {
                    successNotify("Geofence saved successfully");
                    setIsGeofenceMode(false);
                    setSelectedGeofence(null);
                    dispatch(getAllGeofence()); // Refresh list from API
                })
                .catch((error) => {
                    errorNotify("Failed to save geofence");
                });

        }
    };

    const handleRadiusChanged = (id, ref) => {
        if (ref) {
            const newRadius = ref.getRadius();
            const geofence = geofences.find(g => g.id === id);

            if (geofence && Math.abs(geofence.radius - newRadius) > 1) { // 1 meter threshold
                setGeofences(prev => prev.map(geo =>
                    geo.id === id ? { ...geo, radius: newRadius } : geo
                ));

                if (selectedGeofence?.id === id) {
                    setSelectedGeofence(prev => ({ ...prev, radius: newRadius }));
                }
            }
        }
    };

    const handleCenterChanged = (id, newCenter) => {
        const geofence = geofences.find(g => g.id === id);
        if (geofence) {
            const address = `${newCenter.lat.toFixed(5)}, ${newCenter.lng.toFixed(5)}`;
            setGeofences(prev => prev.map(geo =>
                geo.id === id ? { ...geo, center: newCenter, address } : geo
            ));
            if (selectedGeofence?.id === id) {
                setSelectedGeofence(prev => ({ ...prev, center: newCenter, address }));
            }
        }
    };

    return (
        <div className="trip_map_card">
            {!detailView && (
                <div className="trip_map_header">
                    <label className="checkbox_field">
                        <input
                            type="checkbox"
                            checked={viewAllTrips}
                            onChange={(e) => onToggleViewAll(e.target.checked)}
                            disabled={activeTab !== "live"}
                        />
                        <span style={{ opacity: activeTab !== "live" ? 0.5 : 1, cursor: "pointer" }}>View All Trips</span>
                    </label>

                    {
                        viewAllTrips && <>
                            {selectedGeofence ? (
                                <div className="trip_map_header_geofence">
                                    <p>Geofence Radius</p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <input
                                            type="number"
                                            value={Math.round(selectedGeofence.radius / 1000)} // Display in KM
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                if (val > 0) {
                                                    const newRadius = val * 1000;
                                                    handleRadiusChanged(selectedGeofence.id, { getRadius: () => newRadius });
                                                }
                                            }}
                                            style={{ width: "70px", padding: "0px 5px", borderRadius: "5px", border: "1px solid #ccc" }}
                                        />
                                        <span style={{ color: "#3D3F5C", fontSize: "14px" }}>km</span>
                                    </div>
                                    <FaRegSave
                                        color="#3c8f40d4"
                                        size={20}
                                        style={{ cursor: "pointer", marginLeft: "10px" }}
                                        onClick={handleSaveGeofence}
                                        title="Save Geofence"
                                    />
                                    <FaRegTrashAlt
                                        color="red"
                                        size={18}
                                        style={{ cursor: "pointer", marginLeft: "10px" }}
                                        onClick={handleDeleteGeofence}
                                        title="Delete Geofence"
                                    />

                                    <div style={{ width: '1px', height: '20px', background: '#ccc', margin: '0 10px' }}></div>

                                    <button
                                        onClick={() => {
                                            setIsGeofenceMode(false);
                                            setSelectedGeofence(null);
                                        }}
                                        style={{
                                            backgroundColor: "#fff",
                                            color: "#d32f2f",
                                            border: "1px solid #d32f2f",
                                            padding: "6px 16px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            lineHeight: 1
                                        }}
                                    >
                                        Exit
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setIsGeofenceMode(prev => !prev)}
                                    style={{
                                        backgroundColor: isGeofenceMode ? "red" : "#4014B8",
                                        color: isGeofenceMode ? "#fff" : "#fff",
                                        border: "none",
                                        padding: "5px 14px",
                                        borderRadius: "7px",
                                        cursor: "pointer",
                                        fontSize: "14px"
                                    }}
                                >
                                    {isGeofenceMode ? "Exit Geofence Mode" : "Add Geofence"}
                                </button>
                            )}
                        </>
                    }
                </div>
            )}
            <div style={{ flex: 1, minHeight: 0, borderRadius: "8px", overflow: "hidden" }}>
                <FleetOverviewMapLibre
                    geofences={geofences}
                    setGeofences={setGeofences}
                    isGeofenceMode={isGeofenceMode}
                    selectedGeofence={selectedGeofence}
                    setSelectedGeofence={setSelectedGeofence}
                    handleRadiusChanged={handleRadiusChanged}
                    handleCenterChanged={handleCenterChanged}
                    customCenter={customCenter}
                />
            </div>
        </div>
    );
};

export default TripMap;
